import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId, selectPrivileges } from "../../../../redux/authSlice";
import { canViewPayments } from "../../../../Shared/AppDrawer/menuPrivileges";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import LeadFiltersDrawer, {
  LeadFilterValues,
  countActiveLeadFilters,
} from "../../../../ui/LeadFiltersDrawer";
import YhSourceIcon from "../../../../ui/YhSourceIcon";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import StageReasonModal from "../../../../global/components/StageReasonModal";
import { useLeadRealtimeRefresh } from "../../../../utils/useLeadRealtimeRefresh";
import { RootState } from "../../../../utils/store";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";
import urls from "../../../../global/constants/UrlConstants";
import {
  getAllMyLead,
  getAllMyLeadContactStage,
  getAllForms,
  getAllSource,
  getReminder,
  getReminderData,
  getRemark,
  saveReminder,
  saveRemark,
} from "./ShowAllService";
import {
  changeStage,
  deleteReminder,
} from "../../../AllLeads/components/ShowAll/ShowAllService";
import { listBulkJobs } from "../../../../services/bulkJobService";
import notifiers from "../../../../global/constants/NotificationConstants";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import { store } from "../../../../utils/store";
import LeadDetailsComponent from "../../../../global/components/sidePanel/leadDetail";
import { getStage, getStageType } from "../../../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../../../Shared/Phone/PhoneWithCountryCode";
import { YhDateRangeValue } from "../../../../ui/YhDateTimePicker";
import {
  FILTER_KEYS,
  loadLeadListFilters,
  saveLeadListFilters,
  clearLeadListFilters,
  setLeadListReturnPath,
  expandStageSelection,
  parseListPagination,
  withListPagination,
  PersistedMyLeadFilters,
} from "../../../../helpers/leadListFilters";
import {
  isAssignNotification,
  isNiOrLostStage,
} from "../../../../helpers/salesLeadHelpers";
import {
  compareLeadsNewestFirst,
  formatLeadDateParts,
  isFreshLeadToday,
  leadArrivalTime,
  patchLeadStageOnRow,
  relativeLeadAgeLabel,
} from "../../../../helpers/leadDate";

type StageTab = { label: string; isParent: boolean };

/** Underline tab strip — matches the Tailwind pattern used across MyDashboard. */
const UnderlineTabs = ({
  tabs,
  value,
  onChange,
}: {
  tabs: StageTab[];
  value: number;
  onChange: (index: number) => void;
}) => (
  <div className="overflow-x-auto border-b border-[#EEF0F5] [scrollbar-width:thin]">
    <div className="flex w-max min-w-full flex-nowrap gap-1">
      {tabs.map((tab, index) => (
        <button
          key={`${index}-${tab.label}`}
          type="button"
          onClick={() => onChange(index)}
          className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors ${
            value === index
              ? "border-b-2 border-[#A02448] text-[#A02448]"
              : "border-b-2 border-transparent text-[#6B7280] hover:text-[#A02448]"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  </div>
);

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

function stageTabIndex(stage: any) {
  const n = Number(stage?.tabIndex);
  return Number.isFinite(n) ? n : null;
}

/** Meta Stage parent categories only — not sub-stages. Ordered by tabIndex. */
function stageTabsFromTree(tree: any[]): StageTab[] {
  const tabs: StageTab[] = [];
  const seen = new Set<string>();
  [...(tree || [])]
    .sort((a, b) => {
      const ai = stageTabIndex(a);
      const bi = stageTabIndex(b);
      if (ai != null && bi != null && ai !== bi) return ai - bi;
      if (ai != null && bi == null) return -1;
      if (ai == null && bi != null) return 1;
      return 0;
    })
    .forEach((stage: any) => {
      const label = String(stage?.stage_Name || "").trim();
      if (!label) return;
      const key = label.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      tabs.push({ label, isParent: true });
    });
  return tabs;
}

type TabFilter = {
  search: string;
  formLead: string[];
  stage: string[];
  sources: string[];
  assignment: string;
  uploadLeadIds: string[];
  selectedDateRange: YhDateRangeValue;
};

const emptyTabFilter = (): TabFilter => ({
  search: "",
  formLead: [],
  stage: [],
  sources: [],
  assignment: "all",
  uploadLeadIds: [],
  selectedDateRange: { from: null, to: null },
});

const flattenStageData = (data: any[]): { label: string; isParent: boolean }[] => {
  const result: { label: string; isParent: boolean }[] = [];
  (data || []).forEach((stage: any) => {
    const name = String(stage?.stage_Name || "").trim();
    if (!name) return;
    const kids = Array.isArray(stage?.sub_Stage_name)
      ? stage.sub_Stage_name
      : [];
    if (kids.length) {
      result.push({ label: name, isParent: true });
      kids.forEach((sub: any) => {
        if (sub?.stage_Name) {
          result.push({ label: sub.stage_Name, isParent: false });
        }
      });
    } else {
      result.push({ label: name, isParent: false });
    }
  });
  return result;
};

const leadActivityTime = (row: any) => leadArrivalTime(row);
const relativeLeadTag = (dateTime: any): string | null =>
  relativeLeadAgeLabel(dateTime);
const sortLeadsByAssigned = compareLeadsNewestFirst;

const avatarPalette = [
  { bg: "#F9F0F4", color: "#6B1630" },
  { bg: "#DBEAFE", color: "#2563EB" },
  { bg: "#D1FAE5", color: "#059669" },
  { bg: "#FFEDD5", color: "#EA580C" },
  { bg: "#FEF3C7", color: "#D97706" },
  { bg: "#FCE7F3", color: "#DB2777" },
  { bg: "#CCFBF1", color: "#0D9488" },
];

const avatarFor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < String(name).length; i++) {
    hash = String(name).charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

const renderDateCell = (value: any, fallbacks: unknown[] = []) => {
  const parts = formatLeadDateParts(value, fallbacks);
  if (!parts) {
    return <span className="text-xs text-[#9CA3AF]">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 text-xs leading-snug text-[#6B7280]">
      <div className="font-medium text-[#111827]">{parts.date}</div>
      <div>{parts.time}</div>
    </div>
  );
};

const ShowAll = () => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const history = useHistory();
  const location = useLocation();
  const privileges = useSelector(selectPrivileges) || {};
  const showPaymentColumn = canViewPayments(privileges);
  const authUser = useSelector((s: any) => s.auth) || {};
  const isAdminUser = !!(authUser.isAdmin || authUser.isSuperAdmin);
  const notifications = useSelector(
    (s: RootState) => s.notification?.notifications || []
  );
  const [stageReason, setStageReason] = useState<{
    open: boolean;
    stage: string;
    lead: any;
  }>({ open: false, stage: "", lead: null });
  const [savingStageReason, setSavingStageReason] = useState(false);
  const lastAssignNotifId = useRef<string>("");

  const persisted =
    loadLeadListFilters<PersistedMyLeadFilters>(
      FILTER_KEYS.myLead,
      String(store.getState().auth?.userId || "")
    ) || {};

  const [selectedTab, setSelectedTab] = useState(
    () =>
      typeof persisted.selectedTab === "number" ? persisted.selectedTab : 0
  );
  const [stageTabs, setStageTabs] = useState<StageTab[]>([]);
  const tabItems = useMemo<StageTab[]>(
    () => [{ label: "All", isParent: true }, ...stageTabs],
    [stageTabs]
  );
  const [tabFilters, setTabFilters] = useState<{ [key: number]: TabFilter }>(
    () => {
      const base: { [key: number]: TabFilter } = {
        0: emptyTabFilter(),
      };
      if (persisted.tabFilters) {
        Object.keys(persisted.tabFilters).forEach((k) => {
          const idx = Number(k);
          if (Number.isNaN(idx)) return;
          base[idx] = { ...emptyTabFilter(), ...persisted.tabFilters![k] };
        });
      }
      return base;
    }
  );

  const [paginationModel, setPaginationModel] = useState(() =>
    parseListPagination(location.search, {
      page: persisted.page || 0,
      pageSize: persisted.pageSize || 25,
    })
  );
  const [userDataSource1, setUserDataSource1] = useState<any[]>([]);
  const [metaData, setMetaData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const [formName, setFormName] = useState<any[]>([]);
  const [allSatgedata, setAllStagedata] = useState<any[]>([]);
  const [filteredStageData, setFilteredStageData] = useState<any[]>([]);
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [bulkSetOptions, setBulkSetOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(
    () => persisted.searchInput || ""
  );
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [dynamicGridValue, setDynamicGridValue] = useState<any>(null);
  const [showSideBar, setShowSideBar] = useState(false);
  const [addinationalValue, setaddinationalValue] = useState<any>();
  const [, setLeadData] = useState<any>();

  const currentDateTime = new Date().toISOString().slice(0, 16);
  const [reminderStatus, setReminderStatus] = useState<any>("");
  const [reminderData, setReminderData] = useState<any>({ date: currentDateTime });
  const [responseValuereminder, setResponseValuereminder] = useState<any>([]);
  const [responseValueremark, setResponseValueremark] = useState<any>([]);
  const [reminders, setReminders] = useState<{ [key: string]: any }>({});

  const filters = tabFilters[selectedTab] || emptyTabFilter();

  const stageOptions = useMemo(() => {
    const tree = selectedTab === 0 ? allSatgedata : filteredStageData;
    return flattenStageData(tree).filter((opt) => !opt.isParent);
  }, [selectedTab, allSatgedata, filteredStageData]);

  const activeFilterCount = useMemo(() => {
    if (!filters) return 0;
    return countActiveLeadFilters({
      formIds: filters.formLead || [],
      sources: filters.sources || [],
      stages: filters.stage || [],
      assignment: filters.assignment || "all",
      uploadLeadIds: filters.uploadLeadIds || [],
      dateRange: filters.selectedDateRange || { from: null, to: null },
    });
  }, [filters]);

  // Forms are only for the filter dropdown — slim endpoint (no N+1 enrichment)
  useEffect(() => {
    if (!filtersOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [formsRes, sourceRes] = await Promise.all([
          getAllForms(),
          getAllSource().catch(() => null),
        ]);
        if (cancelled) return;
        setFormName(
          (formsRes.data || [])
            .filter((item: any) => item.status === "1")
            .map((i: any) => ({ id: i._id, label: i.form_name }))
        );
        if (sourceRes?.data) {
          const seen = new Set<string>();
          const names: string[] = [];
          for (const e of sourceRes.data) {
            const name = String(e?.source_name || "").trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            names.push(name);
          }
          setSourceOptions(names);
        }
      } catch (error) {
        console.error("Error fetching forms:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filtersOpen]);

  useEffect(() => {
    if (!filtersOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const orgId =
          activeOrganizationId &&
          activeOrganizationId !== "all" &&
          activeOrganizationId !== "*"
            ? String(activeOrganizationId)
            : "";
        const res = await listBulkJobs({
          type: "leads",
          status: "completed,partial,failed",
          limit: 100,
          ...(orgId ? { organizationId: orgId } : {}),
        });
        if (cancelled) return;
        const items = Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.items)
            ? res.items
            : Array.isArray(res?.data)
              ? res.data
              : [];
        setBulkSetOptions(
          items
            .filter((j: any) => Number(j?.totals?.success || 0) > 0)
            .map((j: any) => ({
              value: String(j._id),
              label:
                String(j.setName || "").trim() ||
                String(j.subSource || "").trim() ||
                String(j.originalFileName || "").trim() ||
                `Set ${String(j._id).slice(-6)}`,
            }))
        );
      } catch (_) {
        if (!cancelled) setBulkSetOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filtersOpen, activeOrganizationId]);

  // Persist filters across lead-details navigation
  useEffect(() => {
    saveLeadListFilters(
      FILTER_KEYS.myLead,
      {
      selectedTab,
      tabFilters,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      searchInput,
    } as PersistedMyLeadFilters,
      String(store.getState().auth?.userId || "")
    );
  }, [selectedTab, tabFilters, paginationModel.page, paginationModel.pageSize, searchInput]);

  // Keep page/pageSize in the URL so refresh / back from lead details stays put
  useEffect(() => {
    const next = withListPagination(
      location.pathname || urls.mylead,
      paginationModel.page,
      paginationModel.pageSize,
      location.search
    );
    const current = `${location.pathname || ""}${location.search || ""}`;
    if (next !== current) {
      history.replace(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const remindersStarted = useRef(false);
  const loadReminders = () => {
    if (remindersStarted.current) return;
    remindersStarted.current = true;
    getReminderData()
      .then((response5) => {
        const map: Record<string, any[]> = {};
        (response5?.data || []).forEach((r: any) => {
          const leadId = String(r?.leadId || "").trim();
          if (!leadId || leadId === "NA") return;
          if (!map[leadId]) map[leadId] = [];
          map[leadId].push(r);
        });
        setReminders(map);
      })
      .catch((error) => {
        remindersStarted.current = false;
        console.error("Error fetching reminders:", error);
      });
  };

  useEffect(() => {
    let cancelled = false;
    getStage()
      .then((stageTree) => {
        if (cancelled) return;
        const tree = Array.isArray(stageTree?.data) ? stageTree.data : [];
        setAllStagedata(tree);
        setStageTabs(stageTabsFromTree(tree));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selectedTab >= tabItems.length) setSelectedTab(0);
  }, [tabItems.length, selectedTab]);

  useEffect(() => {
    if (!filtersOpen) return;
    let cancelled = false;
    const current = tabItems[selectedTab];
    if (!current || current.label === "All") {
      setFilteredStageData([]);
      return;
    }
    if (!current.isParent) {
      setFilteredStageData([{ stage_Name: current.label }]);
      return;
    }
    getStageType(current.label)
      .then((resData) => {
        if (!cancelled) setFilteredStageData(resData?.data || []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [filtersOpen, selectedTab, tabItems]);

  useEffect(() => {
    setSearchInput(tabFilters[selectedTab]?.search || "");
  }, [selectedTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const tableDataShowHandler = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    try {
      if (!silent) {
        setApiResponse(true);
        setLoading(true);
      }

      const currentFilters = tabFilters[selectedTab];
      const stageFilter = expandStageSelection(
        currentFilters?.stage,
        allSatgedata
      );
      const payload = {
        userId: store.getState().auth.userId,
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: currentFilters?.search || "",
        formId: currentFilters?.formLead?.length
          ? currentFilters.formLead.join(",")
          : "",
        stage: stageFilter.length ? stageFilter.join(",") : "",
        source: currentFilters?.sources?.length
          ? currentFilters.sources.join(",")
          : "",
        assignment:
          currentFilters?.assignment && currentFilters.assignment !== "all"
            ? currentFilters.assignment
            : "",
        uploadLeadId: currentFilters?.uploadLeadIds?.length
          ? currentFilters.uploadLeadIds.join(",")
          : "",
        start_date: currentFilters?.selectedDateRange?.from || "",
        end_date:
          currentFilters?.selectedDateRange?.to ||
          currentFilters?.selectedDateRange?.from ||
          "",
        organizationId: (() => {
          const orgId = String(store.getState().auth?.activeOrganizationId || "").trim();
          return orgId && orgId !== "all" && orgId !== "*" ? orgId : "";
        })(),
      };

      const currentTab = tabItems[selectedTab];
      const tabName = String(currentTab?.label || "").trim();
      let response;
      if (!tabName || tabName === "All") {
        response = await getAllMyLead(payload);
      } else if (currentTab?.isParent) {
        response = await getAllMyLeadContactStage({
          ...payload,
          parentStage: tabName,
        });
      } else {
        const stageNames = expandStageSelection([tabName], allSatgedata);
        response = await getAllMyLead({
          ...payload,
          stage: (stageNames.length ? stageNames : [tabName]).join(","),
        });
      }

      const metaRaw = response?.metadata;
      const metaObj = Array.isArray(metaRaw) ? metaRaw[0] || {} : metaRaw || {};
      setMetaData({
        ...metaObj,
        total: metaObj?.total ?? 0,
      });

      const rows = (Array.isArray(response?.data) ? response.data : [])
        .slice()
        .sort(sortLeadsByAssigned);
      setUserDataSource1(rows);
      loadReminders();
    } catch (error: any) {
      if (!silent) {
        setUserDataSource1([]);
        setMetaData({ total: 0, page: 0 });
        openErrorNotification(isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR);
      }
    } finally {
      if (!silent) {
        setApiResponse(false);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      tableDataShowHandler();
    }, 40);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, tabFilters, paginationModel, activeOrganizationId, tabItems]);

  const authUserId = useSelector((state: RootState) => state.auth.userId);
  useLeadRealtimeRefresh({
    userId: authUserId,
    showToast: true,
    onSilentRefresh: () => {
      void tableDataShowHandler({ silent: true });
    },
  });

  useEffect(() => {
    const latest = (notifications || []).find((n: any) =>
      isAssignNotification(n)
    );
    const id = String(latest?._id || "");
    if (!id || id === lastAssignNotifId.current) return;
    lastAssignNotifId.current = id;
    void tableDataShowHandler({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const updateTabFilter = (patch: Partial<TabFilter>) => {
    setTabFilters((prev) => ({
      ...prev,
      [selectedTab]: {
        ...emptyTabFilter(),
        ...prev[selectedTab],
        ...patch,
      },
    }));
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  };

  const handleSearch = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      updateTabFilter({ search: value });
    }, 350);
  };

  const applyDrawerFilters = (next: LeadFilterValues) => {
    updateTabFilter({
      formLead: next.formIds,
      sources: next.sources,
      stage: next.stages,
      assignment: next.assignment || "all",
      uploadLeadIds: next.uploadLeadIds,
      selectedDateRange: next.dateRange || { from: null, to: null },
    });
  };

  const clearAllFilters = () => {
    setSearchInput("");
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    updateTabFilter(emptyTabFilter());
    clearLeadListFilters(
      FILTER_KEYS.myLead,
      String(store.getState().auth?.userId || "")
    );
  };

  const onClickHandler = () => {
    history.push(urls.MYLEADADDPAGE);
  };

  const openCustomer = (params: any) => {
    const lead = params?.row || params;
    const leadId = lead?._id || params?._id;
    if (!leadId) return;
    try {
      sessionStorage.setItem(`all-lead-details-${leadId}`, JSON.stringify(lead));
      const navList = (userDataSource1 || [])
        .filter((item: any) => item?._id)
        .map((item: any) => ({ ...item }));
      sessionStorage.setItem("all-leads-nav-list", JSON.stringify(navList));
    } catch {
      // ignore storage failures
    }
    const returnPath = withListPagination(
      urls.mylead,
      paginationModel.page,
      paginationModel.pageSize,
      location.search
    );
    setLeadListReturnPath(returnPath);
    history.push(urls.ALLLEADDETAILS.replace(":id", leadId), {
      lead,
      type: params?.type || "",
      from: returnPath,
    });
  };

  const getReminderInfo = async (id: any) => {
    try {
      const response = await getReminder(id);
      if (response.success === 1) {
        setResponseValuereminder((prev: any) => ({ ...prev, value: response?.data }));
      } else {
        setResponseValuereminder([]);
      }
    } catch {
      // silently ignore — reminder panel simply stays empty
    }
  };

  const getRemarkInfo = async (id: any) => {
    try {
      const response = await getRemark(id);
      if (response.success === 1) {
        setResponseValueremark((prev: any) => ({ ...prev, value: response?.data }));
      } else {
        setResponseValueremark([]);
      }
    } catch {
      // silently ignore — notes panel simply stays empty
    }
  };

  const handleRowClick = (row: any) => {
    setShowSideBar(!showSideBar);
    setDynamicGridValue(row);
    setaddinationalValue(row?.dynamicFields || []);
    getRemarkInfo(row?._id);
    getReminderInfo(row?._id);
  };

  const patchLeadStageInPlace = (
    leadId: string,
    stage: string,
    extras?: { AssignToUser?: any[] }
  ) => {
    const uid = String(store.getState().auth?.userId || "");
    const apply = (row: any) =>
      String(row?._id) === String(leadId)
        ? patchLeadStageOnRow(row, stage, uid, extras)
        : row;
    setUserDataSource1((prev: any[]) => (prev || []).map(apply));
    setDynamicGridValue((prev: any) => (prev ? apply(prev) : prev));
  };

  const handleAutocompleteChange = async (
    name: string,
    value: any,
    data: any,
    opts?: { reasonAlreadySaved?: boolean; reason?: string }
  ) => {
    if (name !== "stage") return;
    if (!value || String(value) === String(data?.stage || "")) return;
    if (isNiOrLostStage(value) && !opts?.reasonAlreadySaved) {
      setStageReason({ open: true, stage: value, lead: data });
      return;
    }
    const body: any = { id: data?._id, stage: value };
    if (opts?.reason) {
      body.reason = opts.reason;
      body.stageReason = opts.reason;
    }
    try {
      setApiResponse(true);
      const response = await changeStage(body);
      if (response.success === 1) {
        patchLeadStageInPlace(String(data?._id), value, {
          AssignToUser: response?.AssignToUser,
        });
        openSuccessNotification(response?.message);
      } else {
        openErrorNotification(response?.message || "Failed to update stage");
      }
    } catch (error: any) {
      openErrorNotification(isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR);
    } finally {
      setApiResponse(false);
    }
  };

  const confirmStageReason = async (reason: string) => {
    const lead = stageReason.lead;
    if (!lead?._id || !stageReason.stage) return;
    try {
      setSavingStageReason(true);
      const response = await changeStage({
        id: lead._id,
        stage: stageReason.stage,
        reason,
        stageReason: reason,
      });
      if (response?.success === 1) {
        patchLeadStageInPlace(String(lead._id), stageReason.stage, {
          AssignToUser: response?.AssignToUser,
        });
        openSuccessNotification(response?.message || "Stage updated");
      } else {
        openErrorNotification(response?.message || "Failed to update stage");
      }
      setStageReason({ open: false, stage: "", lead: null });
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSavingStageReason(false);
    }
  };

  const handelSideSave = async (body: any) => {
    try {
      setApiResponse(true);
      const response = await saveReminder(body);
      if (response.success === 1) {
        const response5 = await getReminder(body.leadId);
        setReminders((prev) => ({ ...prev, [body.leadId]: response5.data }));
        tableDataShowHandler();
      }
    } catch (error: any) {
      openErrorNotification(isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR);
    } finally {
      setApiResponse(false);
    }
  };

  const handelSideSaveRemark = async (body: any) => {
    const payload = {
      leadId: body.leadId,
      notes: body.notes_remark,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      title: "",
    };
    try {
      setApiResponse(true);
      await saveRemark(payload);
      setReminderData((prev: any) => ({ ...prev, notes_remark: "" }));
      getRemarkInfo(body.leadId);
      tableDataShowHandler();
    } catch (error: any) {
      openErrorNotification(isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR);
    } finally {
      setApiResponse(false);
    }
  };

  const handelRemoveReminder = async (item: any) => {
    try {
      setApiResponse(true);
      await deleteReminder(item);
      tableDataShowHandler();
    } catch (error: any) {
      openErrorNotification(isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR);
    } finally {
      setApiResponse(false);
    }
  };

  const handleChangeRadio = (event: any) => {
    setReminderStatus(event.target.value || event.target.defaultValue);
  };

  const handleDateTimeChange = (event: any) => {
    setReminderData((prev: any) => ({
      ...prev,
      date: event.target.value.split("T")[0],
      time: event.target.value.split("T")[1],
    }));
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Leads",
      field: "leadName",
      minWidth: 220,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row;
        const name = row?.leadName || "?";
        const initial = String(name).trim().charAt(0).toUpperCase() || "?";
        const avatar = avatarFor(name);
        return (
          <div
            className="action-button flex cursor-pointer items-center gap-3"
            data-action-button="true"
            onClick={(e) => {
              e.stopPropagation();
              openCustomer(row);
            }}
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold"
              style={{ backgroundColor: avatar.bg, color: avatar.color }}
            >
              {initial}
            </span>
            <div className="flex min-w-0 flex-col items-start gap-0.5">
              <span className="truncate text-sm font-semibold capitalize text-[#A02448]">
                {name}
              </span>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="shrink-0 text-xs text-[#9CA3AF]">{row?.uid || "—"}</span>
                {(() => {
                  const arrival = leadActivityTime(row);
                  const age = relativeLeadTag(arrival);
                  const fresh = isFreshLeadToday(row);
                  if (!fresh && !age) return null;
                  return (
                    <>
                      {fresh ? (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          Fresh
                        </span>
                      ) : null}
                      {age ? (
                        <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                          {age}
                        </span>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Mobile",
      field: "leadPhone",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => {
        const display = formatPhoneKeepingCountryCode(
          resolveLeadPhoneForDisplay(params.row)
        );
        return (
          <span
            className="font-mono text-sm tracking-wide text-[#374151]"
            title={display}
          >
            {display}
          </span>
        );
      },
    },
    ...(showPaymentColumn
      ? [
          {
            headerName: "Payment",
            field: "paymentStatus",
            minWidth: 128,
            width: 128,
            renderCell: (params: YhTableCellParams) => {
        const row = params.row;
        const rawStatus = String(
          row?.paymentStatus ||
            row?.easebuzzStatus ||
            row?.payment?.paymentStatus ||
            row?.payment?.easebuzzStatus ||
            ""
        ).trim();
        const status = rawStatus.toLowerCase();
        const amount =
          row?.paymentAmount ||
          row?.amount ||
          row?.payment?.amount ||
          "";

        if (!status && !amount) {
          return (
            <div className="w-[96px] text-left text-sm text-[#9CA3AF]">—</div>
          );
        }

        let badgeStyle = "bg-[#F3F4F6] text-[#4B5563]";
        let label = rawStatus || "—";

        if (status === "paid" || status === "success" || status === "successful") {
          badgeStyle = "bg-[#DCFCE7] text-[#166534]";
          label = "Paid";
        } else if (
          status === "pending" ||
          status === "pre-initiated" ||
          status === "preinitiated"
        ) {
          badgeStyle = "bg-[#FEF3C7] text-[#92400E]";
          label = "Pending";
        } else if (status === "unpaid") {
          badgeStyle = "bg-[#F9F0F4] text-[#3730A3]";
          label = "Unpaid";
        } else if (status === "failed" || status === "failure") {
          badgeStyle = "bg-[#FEE2E2] text-[#991B1B]";
          label = "Failed";
        } else if (
          status === "bounced" ||
          status === "dropped" ||
          status === "cancelled"
        ) {
          badgeStyle = "bg-[#DBEAFE] text-[#1E40AF]";
          label = status.charAt(0).toUpperCase() + status.slice(1);
        } else if (rawStatus) {
          label = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
        }

        return (
          <div className="flex w-[96px] flex-col items-start gap-0.5">
            {status ? (
              <span
                className={`inline-flex h-6 w-full items-center justify-center gap-1 rounded-full px-2 text-[11px] font-bold leading-none ${badgeStyle}`}
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80" />
                {label}
              </span>
            ) : null}
            {amount ? (
              <span className="w-full font-mono text-xs font-semibold tabular-nums text-[#374151]">
                ₹{Number(amount).toLocaleString("en-IN")}
              </span>
            ) : null}
          </div>
        );
      },
          },
        ]
      : []),
    {
      headerName: "Property",
      field: "project_name",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="truncate text-sm font-semibold capitalize text-[#111827]">
            {params.row.project_name || "—"}
          </span>
          <span className="truncate text-xs capitalize text-[#6B7280]">
            {params.row.projecttype_name || ""}
          </span>
        </div>
      ),
    },
    {
      headerName: "Reminder",
      field: "reminder",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const leadId = params.row._id;
        const reminderList = reminders[leadId];
        if (reminderList && reminderList.length > 0) {
          const latestReminder = reminderList.reduce(
            (latest: { date: any; time: any }, current: { date: any; time: any }) => {
              const latestDateTime = new Date(`${latest.date}T${latest.time}`);
              const currentDateTime = new Date(`${current.date}T${current.time}`);
              return currentDateTime > latestDateTime ? current : latest;
            }
          );
          const reminderDateTime = new Date(`${latestReminder.date}T${latestReminder.time}`);
          const isExpired = reminderDateTime < new Date();
          return isExpired ? (
            <span className="inline-flex h-7 items-center justify-center rounded-full border border-red-200 bg-red-50 px-2.5 text-[11px] font-semibold text-red-600">
              Expired
            </span>
          ) : (
            <span className="inline-flex h-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 text-[11px] font-semibold text-emerald-600">
              Reminder
            </span>
          );
        }
        return <div className="h-7 w-[70px]" />;
      },
    },
    {
      headerName: "Form Name",
      field: "form_name",
      minWidth: 170,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">{params.row?.form_name || "—"}</span>
      ),
    },
    {
      headerName: "Source",
      field: "source",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const source = params.row?.source || "—";
        return (
          <div className="flex items-center gap-2.5">
            <YhSourceIcon source={source} />
            <span className="truncate text-sm font-medium capitalize text-[#374151]">
              {source}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Stage",
      field: "stage",
      minWidth: 130,
      renderCell: (params: YhTableCellParams) => (
        <span className="inline-flex h-7 items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 text-xs font-semibold capitalize text-orange-600">
          {params.row?.stage || "—"}
        </span>
      ),
    },
    {
      headerName: "Created Date",
      field: "createdAt",
      minWidth: 130,
      renderCell: (params: YhTableCellParams) =>
        renderDateCell(leadActivityTime(params.row)),
    },
    {
      headerName: "Updated Date",
      field: "updatedAt",
      minWidth: 130,
      renderCell: (params: YhTableCellParams) =>
        renderDateCell(params.value, [
          params.row?.createdAt,
          params.row?.date,
        ]),
    },
  ];

  // This page is route-gated; always render the table so privilege gaps don't hide assigned leads
  const canView = true;
  const canCreate =
    isAdminUser ||
    privileges?.["My Leads"]?.includes("create") ||
    privileges?.["My Leads"]?.includes("full control") ||
    privileges?.["Leads"]?.includes("create") ||
    privileges?.["Leads"]?.includes("full control") ||
    privileges?.["All Lead"]?.includes("create") ||
    privileges?.["All Lead"]?.includes("full control");

  return (
    <div className="flex h-full min-h-0 w-full gap-0 overflow-hidden">
      {/* Main table area — shrinks when drawer opens */}
      <div
        className={`h-full min-h-0 min-w-0 transition-[flex-basis,width,margin] duration-300 ease-in-out ${
          dynamicGridValue ? "w-full flex-[1_1_0%] pr-0 md:pr-3" : "w-full flex-[1_1_100%]"
        }`}
      >
        <div className="flex h-full min-h-0 w-full flex-col">
          {/* Page header */}
          <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#111827]">My Leads</h1>
              <p className="mt-1 text-sm text-[#6B7280]">
                View &amp; manage the leads assigned to you.
              </p>
            </div>

            {canCreate ? (
              <button
                type="button"
                onClick={onClickHandler}
                className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl bg-[#A02448] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#841C39]"
              >
                + Add Lead
              </button>
            ) : null}
          </div>

          {/* Stage tabs */}
          <div className="mb-3 shrink-0">
            <UnderlineTabs tabs={tabItems} value={selectedTab} onChange={setSelectedTab} />
          </div>

          {/* Search + Filters button */}
          <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
            <div className="min-w-[240px] flex-1 sm:max-w-[380px]">
              <YhInput
                placeholder="Search by Id, Name, Email and Phone"
                value={searchInput}
                type="search"
                id="my-lead-search"
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus={isSearchFocused}
                startAdornment={<SearchSvg />}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                containerClassName="w-full"
              />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-semibold text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC]"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                </svg>
                Filters
                {activeFilterCount > 0 ? (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#A02448] px-1.5 text-[11px] font-bold text-white">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
              {activeFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="shrink-0 whitespace-nowrap px-2 text-sm font-semibold text-[#A02448] hover:underline"
                >
                  Clear All
                </button>
              ) : null}
            </div>
          </div>

          <LeadFiltersDrawer
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            title="My Lead filters"
            value={{
              formIds: filters?.formLead || [],
              sources: filters?.sources || [],
              stages: filters?.stage || [],
              assignment: filters?.assignment || "all",
              uploadLeadIds: filters?.uploadLeadIds || [],
              dateRange: filters?.selectedDateRange || { from: null, to: null },
            }}
            onApply={applyDrawerFilters}
            onClear={clearAllFilters}
            formOptions={formName.map((option: any) => ({
              label: option.label,
              value: String(option.id),
            }))}
            sourceOptions={sourceOptions.map((s) => ({ label: s, value: s }))}
            stageOptions={stageOptions.map((option) => ({
              label: option.label,
              value: option.label,
            }))}
            bulkSetOptions={bulkSetOptions}
          />

          <div className="relative min-h-0 flex-1">
            {canView ? (
              <YhTable
                columns={columns}
                rows={userDataSource1}
                getRowId={(row) => row._id}
                rowHeight={80}
                loading={loading}
                emptyMessage={
                  isAdminUser
                    ? "No leads found for this organization. Check filters or All Leads."
                    : authUser?.userName
                      ? `No leads assigned to ${authUser.userName}. Assign a member on All Leads to see them here.`
                      : "No leads assigned to you"
                }
                className="h-full min-h-[280px]"
                onRowClick={(row, e) => {
                  const targetElement = e.target as HTMLElement;
                  if (
                    targetElement?.classList?.contains("action-button") ||
                    targetElement?.closest?.(".action-button") ||
                    targetElement?.closest?.("[data-action-button]")
                  ) {
                    return;
                  }
                  handleRowClick(row);
                }}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                totalRows={metaData?.total || 0}
                onPageChange={(p) => setPaginationModel((prev) => ({ ...prev, page: p }))}
                onPageSizeChange={(s) => setPaginationModel({ pageSize: s, page: 0 })}
              />
            ) : null}
          </div>

          {apiResponse && <CustomLoader apiResponse={false} />}
        </div>
      </div>

      {/* Side drawer — pushes in from the right */}
      <aside
        aria-hidden={!dynamicGridValue}
        className={`relative h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out ${
          dynamicGridValue ? "w-[280px] opacity-100 sm:w-[300px] xl:w-[340px]" : "pointer-events-none w-0 opacity-0"
        }`}
      >
        <div
          className={`relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition-transform duration-300 ease-in-out ${
            dynamicGridValue ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ width: "100%", minWidth: dynamicGridValue ? undefined : 0 }}
        >
          <button
            type="button"
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
            onClick={() => {
              setReminderStatus("");
              setDynamicGridValue(null);
              setShowSideBar(false);
            }}
            aria-label="Close lead details"
          >
            <CloseIcon width={"12px"} height={"12px"} />
          </button>

          <div className="h-full w-full overflow-auto p-4 pt-12 [scrollbar-width:thin] [scroll-padding-bottom:96px] sm:min-w-[300px] xl:min-w-[340px]">
            {dynamicGridValue ? (
              <LeadDetailsComponent
                dynamicGridValue={dynamicGridValue}
                handleAutocompleteChange={handleAutocompleteChange}
                handleChangeRadio={handleChangeRadio}
                reminderStatus={reminderStatus}
                setReminderStatus={setReminderStatus}
                setReminderData={setReminderData}
                currentDateTime={currentDateTime}
                handleDateTimeChange={handleDateTimeChange}
                handelSideSave={handelSideSave}
                reminderData={reminderData}
                responseValuereminder={responseValuereminder}
                addinationalValue={addinationalValue}
                isMyLead={true}
                responseValueremark={responseValueremark}
                handelSideSaveRemark={handelSideSaveRemark}
                handelRemoveReminder={handelRemoveReminder}
                onRemindersChange={(rows: any[]) => {
                  setResponseValuereminder({ value: rows });
                  const leadId = String(dynamicGridValue?._id || "");
                  if (leadId) {
                    setReminders((prev: any) => ({ ...prev, [leadId]: rows }));
                  }
                }}
                openCustomer={openCustomer}
                setLeadData={setLeadData}
                allSatgedata={allSatgedata}
              />
            ) : null}
          </div>
        </div>
      </aside>

      <StageReasonModal
        open={stageReason.open}
        stageLabel={stageReason.stage}
        saving={savingStageReason}
        onCancel={() => setStageReason({ open: false, stage: "", lead: null })}
        onConfirm={confirmStageReason}
      />
    </div>
  );
};

export default ShowAll;
